import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './OfferSlider.css';
import api, { BASE_URL } from '../../api';
import { Offer } from '@flooron/shared';

const SLIDE_PALETTES = [
  { bg: '#0f172a', card: '#1e293b', accent: '#38bdf8' },
  { bg: '#1a1a2e', card: '#16213e', accent: '#e94560' },
  { bg: '#0d2137', card: '#1b4332', accent: '#52b788' },
  { bg: '#2d1b69', card: '#4a1942', accent: '#f72585' },
  { bg: '#1a0533', card: '#2d0050', accent: '#ff9f1c' },
];

const OfferSlider: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const res = await api.getActiveOffers(0, 10);
        setOffers(res.data ?? []);
      } catch (err) {
        console.error('Failed to fetch offers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, []);

  const total = offers.length;

  const next = useCallback(() => {
    setImgLoaded(false);
    setCurrent(p => (p + 1) % total);
  }, [total]);

  const prev = useCallback(() => {
    setImgLoaded(false);
    setCurrent(p => (p - 1 + total) % total);
  }, [total]);

  useEffect(() => {
    if (total === 0 || isHovered) return;
    autoPlayRef.current = setInterval(next, 4500);
    return () => { if (autoPlayRef.current) clearInterval(autoPlayRef.current); };
  }, [next, isHovered, total]);

  const onTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchMove  = (e: React.TouchEvent) => { touchEndX.current = e.touches[0].clientX; };
  const onTouchEnd   = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 40) diff > 0 ? next() : prev();
  };

  if (loading) {
    return (
      <section className="os-wrapper os-skeleton">
        <div className="os-shimmer" />
      </section>
    );
  }

  if (total === 0) return null;

  const offer   = offers[current];
  const palette = SLIDE_PALETTES[current % SLIDE_PALETTES.length];
  const imageUrl = offer.bannerImage
    ? `${BASE_URL}/${offer.bannerImage}`
    : null;

  return (
    <section
      className="os-wrapper"
      style={{ '--os-bg': palette.bg, '--os-card': palette.card, '--os-accent': palette.accent } as React.CSSProperties}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <motion.div
        className="os-bg"
        animate={{ backgroundColor: palette.bg }}
        transition={{ duration: 0.7, ease: 'easeInOut' }}
      />

      <div className="os-divider os-divider--top">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" fill="var(--bg)" />
        </svg>
      </div>
      <div className="os-divider os-divider--bottom">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" fill="var(--bg)" />
        </svg>
      </div>

      <div className="os-container">
        <motion.div
          className="os-card"
          animate={{ backgroundColor: palette.card }}
          transition={{ duration: 0.7, ease: 'easeInOut' }}
        >
          <div className="os-grid">
            <div className="os-text">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`text-${current}`}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18 }}
                  transition={{ duration: 0.4 }}
                >
                  <span className="os-badge" style={{ background: palette.accent }}>
                    {offer.isActive ? 'Active Offer' : 'Upcoming'}
                  </span>
                  <h2 className="os-title">{offer.title}</h2>
                  <p className="os-desc">{offer.description}</p>

                  {offer.productPrices?.length > 0 && (
                    <div className="os-prices">
                      {offer.productPrices.slice(0, 3).map((pp, i) => (
                        <span
                          key={i}
                          className="os-price-tag"
                          style={{ borderColor: palette.accent, color: palette.accent }}
                        >
                          Min {pp.minimumQuantity} pcs · ₹{pp.offerPrice}
                        </span>
                      ))}
                    </div>
                  )}

                  <button className="os-btn" style={{ background: palette.accent }}>
                    Shop Now
                  </button>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="os-image-col">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`img-${current}`}
                  className="os-img-wrap"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: imgLoaded || !imageUrl ? 1 : 0.3, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.5 }}
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={offer.title}
                      onLoad={() => setImgLoaded(true)}
                      className="os-img"
                    />
                  ) : (
                    <div className="os-img-placeholder">
                      <span style={{ color: palette.accent }}>No Image</span>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        <div className={`os-controls ${isHovered ? 'os-controls--visible' : ''}`}>
          <button className="os-nav" onClick={prev} aria-label="Previous">
            <ChevronLeft size={18} />
          </button>
          <div className="os-dots">
            {offers.map((_, idx) => (
              <button
                key={idx}
                className={`os-dot ${idx === current ? 'os-dot--active' : ''}`}
                style={idx === current ? { background: palette.accent } : {}}
                onClick={() => { setImgLoaded(false); setCurrent(idx); }}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
          <button className="os-nav" onClick={next} aria-label="Next">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default OfferSlider;